import { Renderer2 } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup } from "@angular/forms";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Params, Router } from "@angular/router";
import { Platform } from "@ionic/angular";
import { KontaktDetail } from "../../api/models/kontakt-detail";

export function getLowerCaseInputValue(input: string): string {
  return input ? input.toLowerCase() : null;
}

export function getStringFilterConditionForDatatable(datavalue: string, filtervalue: string): boolean {
  return (
    (datavalue !== null && filtervalue !== null && datavalue.toLowerCase().indexOf(filtervalue) !== -1) || !filtervalue
  );
}

export function replaceFileExtensionConditionally(fileName: string): string {
  if (fileName.endsWith(".pms")) {
    return fileName.slice(0, -4) + ".eml";
  }

  return fileName;
}

export function isAktArtTechnik(aktArt: string): boolean {
  const technikSparten: string[] = ["Blitzschadenprüfstelle A", "E-Geräteschaden", "Maschinenbruch", "Verkehrstechnik"];
  return technikSparten.includes(aktArt);
}

export function getKontaktBezeichnungByKuerzel(kuerzel: string): string {
  switch (kuerzel) {
    case "AST":
      return "AnspruchstellerIn";
    case "Ansprechpartner":
      return "Ansprechpartner";
    case "FCA":
      return "FCA";
    case "SB2":
      return "Schadenregulierer";
    case "VN":
      return "VersicherungsnehmerIn";
    case "VU":
      return "Versicherung";
    case "VUSB":
      return "SachbearbeiterIn";
    case "Vermittler":
      return "VermittlerIn";
    default:
      return "";
  }
}

export function isKontaktAllowedForEdit(kontaktDetail: KontaktDetail): boolean {
  if (!kontaktDetail?.Name) {
    return false;
  }
  return !(kontaktDetail.Name.startsWith("WEG") || kontaktDetail.Name.startsWith("N.N."));
}

export function translateQuillEditorElements(renderer: Renderer2) {
  function replaceTextWithQuerySelector(querySelector: string, newText: string, pseudoElement?: string) {
    const element = document.querySelector(querySelector);

    if (element) {
      const style = renderer.createElement("style");
      style.textContent = `
        ${querySelector}${pseudoElement} {
          content: '${newText}' !important;
        }
      `;
      renderer.appendChild(document.head, style);
    }
  }

  const translations = [
    {
      selector: 'span.ql-picker-item[data-value="1"]',
      newText: "Überschrift groß",
      pseudoElement: "::before",
    },
    {
      selector: 'span.ql-picker-item[data-value="2"]',
      newText: "Überschrift normal",
      pseudoElement: "::before",
    },
    {
      selector: 'span.ql-picker-item[data-value="3"]',
      newText: "Überschrift klein",
      pseudoElement: "::before",
    },
    {
      selector: "span.ql-header span.ql-picker-item:not([data-value])",
      newText: "Normaltext",
      pseudoElement: "::before",
    },
    {
      selector: 'span.ql-header span.ql-picker-label[data-value="1"]',
      newText: "Ü. groß",
      pseudoElement: "::before",
    },
    {
      selector: 'span.ql-header span.ql-picker-label[data-value="2"]',
      newText: "Ü. normal",
      pseudoElement: "::before",
    },
    {
      selector: 'span.ql-header span.ql-picker-label[data-value="3"]',
      newText: "Ü. klein",
      pseudoElement: "::before",
    },
    {
      selector: "span.ql-header span.ql-picker-label",
      newText: "Normaltext",
      pseudoElement: "::before",
    },
    {
      selector: 'div.ql-tooltip.ql-editing[data-mode="link"]',
      newText: "Link eingeben:",
      pseudoElement: "::before",
    },
    {
      selector: 'div.ql-tooltip.ql-editing[data-mode="link"] a.ql-action',
      newText: "Speichern",
      pseudoElement: "::after",
    },
    {
      selector: "div.ql-tooltip:not([data-mode])",
      newText: "URL besuchen:",
      pseudoElement: "::before",
    },
    {
      selector: "div.ql-tooltip:not([data-mode]) a.ql-action",
      newText: "Bearbeiten",
      pseudoElement: "::after",
    },
    {
      selector: "div.ql-tooltip:not([data-mode]) a.ql-remove",
      newText: "Entfernen",
      pseudoElement: "::before",
    },
  ];

  translations.forEach(({ selector, newText, pseudoElement }) => {
    replaceTextWithQuerySelector(selector, newText, pseudoElement);
  });

  const element = document.querySelector(
    'input[data-formula="e=mc^2"][data-link="https://quilljs.com"][data-video="Embed URL"][placeholder="https://quilljs.com"]',
  );

  if (element) {
    renderer.setProperty(element, "placeholder", "https://faircheck.at/");
  }
}

export function isMobile(platform: Platform): boolean {
  return platform.width() <= 576;
}

export function smallScreenHeight(platform: Platform): boolean {
  return platform.height() <= 500;
}

export function applyMatSortToMatDataSource(matDataSource: MatTableDataSource<any>, matSort: MatSort) {
  matDataSource.sort = matSort;
  matDataSource.data.sort((a, b) => {
    return matSort.direction === "asc"
      ? a[matSort.active] - b[matSort.active]
      : matSort.direction === "desc"
        ? b[matSort.active] - a[matSort.active]
        : a[matSort.active] - b[matSort.active];
  });
  matDataSource._updateChangeSubscription();
}

export function updateTitleByOrginalTitle(originalTitle: string, newTitle: string) {
  const element = document.querySelector(`[title="${originalTitle}"]:not(pdf-shy-button)`);
  if (element) {
    element.setAttribute("title", newTitle);
  }
}

export function getRelevantAktDate(aktTyp: string, kundenterminDatum: any, auftragFaDatum: any) {
  if (aktTyp === "Rechnungsprüfstelle" || !kundenterminDatum) {
    return auftragFaDatum;
  }
  return kundenterminDatum;
}

export function getTimeFromCSharpTicks(ticks: number): {
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
} {
  const totalMilliseconds = ticks / 10000;
  const hours = Math.floor(totalMilliseconds / (60 * 60 * 1000));
  const minutes = Math.floor((totalMilliseconds % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((totalMilliseconds % (60 * 1000)) / 1000);
  const milliseconds = totalMilliseconds % 1000;

  return { hours, minutes, seconds, milliseconds };
}

export function getDateTimeFromCSharpTicks(ticks: number): Date {
  const timeObject = getTimeFromCSharpTicks(ticks);

  return new Date(2024, 0, 1, timeObject.hours, timeObject.minutes, timeObject.seconds, timeObject.milliseconds);
}

export function getCSharpTicksFromDateTime(dateTime: Date): number {
  const hours = dateTime.getHours();
  const minutes = dateTime.getMinutes();
  const seconds = dateTime.getSeconds();
  const milliseconds = dateTime.getMilliseconds();

  const totalMilliseconds = ((hours * 60 + minutes) * 60 + seconds) * 1000 + milliseconds;

  return totalMilliseconds * 10000;
}

export function roundToDecimals(number: number, decimals: number = 2): number {
  const decimalsPower = Math.pow(10, decimals);
  return Math.round((number + Number.EPSILON) * decimalsPower) / decimalsPower;
}

export function navigate(router: Router, event: MouseEvent, route: string, queryParams?: Params) {
  const url = router.serializeUrl(router.createUrlTree([route], { queryParams: queryParams }));
  if (event.ctrlKey || event.metaKey || event.button === 1) {
    window.open(url, "_blank");
  } else {
    router.navigate([route], {
      queryParams: queryParams,
      replaceUrl: true,
    });
  }
}

export function getHrefForPath(path: string): string {
  return this.router.createUrlTree([path], { queryParams: this.queryParams }).toString();
}

export function deepCopyObject<T extends object>(obj: T): T {
  const copy: any = {};
  for (const key of Object.keys(obj)) {
    const value = (obj as any)[key];
    if (Array.isArray(value)) {
      copy[key] = deepCopyArray(value);
    } else if (value && typeof value === "object") {
      copy[key] = deepCopyObject(value);
    } else {
      copy[key] = value;
    }
  }
  return copy;
}

export function deepCopyArray<T>(array: T[]): T[] {
  return array.map((item) => {
    if (Array.isArray(item)) {
      return deepCopyArray(item) as unknown as T;
    } else if (item && typeof item === "object") {
      return { ...deepCopyObject(item) } as T;
    }
    return item;
  });
}

export function deepCopyAbstractControl(control: AbstractControl): AbstractControl {
  if (control instanceof FormControl) {
    return new FormControl(control.value, control.validator, control.asyncValidator);
  }

  if (control instanceof FormGroup) {
    const copiedGroup: { [key: string]: AbstractControl } = {};
    for (const key of Object.keys(control.controls)) {
      copiedGroup[key] = deepCopyAbstractControl(control.controls[key]);
    }
    return new FormGroup(copiedGroup);
  }

  if (control instanceof FormArray) {
    return deepCopyFormArray(control);
  }
}

export function deepCopyFormArray(formArray: FormArray): FormArray {
  const copiedControls = formArray.controls.map((control) => deepCopyAbstractControl(control));
  return new FormArray(copiedControls);
}

export function removeCustomError(control: AbstractControl) {
  if (control?.errors) {
    const errors = { ...control.errors };
    delete errors["customError"];

    control.setErrors(Object.keys(errors).length ? errors : null);
  }
}
